/**
 * Form validator
 */
import { MIN_PASSWORD_LENGTH } from '../constants';

// Constants
// TLD must be letters only (min 2) — matches Booking Manager / class-validator IsEmail.
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[a-zA-Z]{2,}$/;
const PASSWORD_NUMBER_REGEX = /\d/;
const PASSWORD_SYMBOL_REGEX = /[!@#$%^&*(),.?":{}|<>]/;
const ZIP_CODE_REGEX = /^[A-Za-z0-9]{1,20}$/;
const PHONE_NUMBERS_ONLY_REGEX = /^\+?\d+$/;
const REQUIRED_REGISTER_FIELDS = ['first_name', 'last_name', 'salutation', 'country', 'zip_code', 'birthday'];

class FormValidator {
  constructor(messages) {
    this.messages = messages;
  }

  /**
   * Create error object
   */
  createError(key, params = {}) {
    return {
      key,
      message: this.messages.getMessage(key, params),
    };
  }

  /**
   * Check if errors object has any errors
   */
  static hasErrors(errors) {
    return errors && Object.keys(errors).length > 0;
  }

  /**
   * Validate email
   */
  static validateEmail(email) {
    return EMAIL_REGEX.test(email);
  }

  /**
   * Validate phone number (optional leading +, digits, max 13 chars)
   */
  static validatePhoneNumber(phoneNumber) {
    if (!phoneNumber || typeof phoneNumber !== 'string') {
      return false;
    }
    const trimmed = phoneNumber.trim();
    return trimmed.length <= 13 && PHONE_NUMBERS_ONLY_REGEX.test(trimmed);
  }

  /**
   * Validate zip code (letters and digits, max 20 chars)
   */
  static validateZipCode(zipCode) {
    if (!zipCode || typeof zipCode !== 'string') {
      return false;
    }
    return ZIP_CODE_REGEX.test(zipCode.trim());
  }

  /**
   * Validate password
   * Checks minimum length, presence of number and special character
   */
  validatePassword(password, minLength = MIN_PASSWORD_LENGTH) {
    if (!password) {
      return this.createError('error_password_required');
    }

    if (password.length < minLength) {
      return this.createError('error_password_min', { min: minLength });
    }

    if (!PASSWORD_NUMBER_REGEX.test(password)) {
      return this.createError('error_password_no_number');
    }

    if (!PASSWORD_SYMBOL_REGEX.test(password)) {
      return this.createError('error_password_no_symbol');
    }

    return null;
  }

  /**
   * Check password match
   */
  static validatePasswordsMatch(password, confirmPassword) {
    return password === confirmPassword;
  }

  /**
   * Get field-specific error key
   */
  static getFieldErrorKey(fieldName) {
    const fieldErrorMap = {
      salutation: 'error_salutation_required',
      first_name: 'error_first_name_required',
      last_name: 'error_last_name_required',
      country: 'error_country_required',
      zip_code: 'error_zip_code_required',
      birthday: 'error_birthday_required',
      password_confirm: 'error_password_confirm_required',
    };

    return fieldErrorMap[fieldName] || 'error_field_required';
  }

  /**
   * Validate required fields
   */
  validateRequired(fields) {
    const errors = {};

    Object.entries(fields).forEach(([key, value]) => {
      const isEmpty = !value || (typeof value === 'string' && value.trim() === '');
      if (isEmpty) {
        const errorKey = FormValidator.getFieldErrorKey(key);
        errors[key] = this.createError(errorKey);
      }
    });

    return FormValidator.hasErrors(errors) ? errors : null;
  }

  /**
   * Validate password and password confirmation
   */
  validatePasswordFields(password, passwordConfirm) {
    const errors = {};
    const passwordError = this.validatePassword(password);
    if (passwordError) {
      errors.password = passwordError;
    }

    if (
      password
      && passwordConfirm
      && !FormValidator.validatePasswordsMatch(password, passwordConfirm)
    ) {
      errors.password_confirm = this.createError('error_password_match');
    }

    return errors;
  }

  /**
   * Validate age (must be at least 21 years old)
   * @param {string} birthday - Birthday in format "MM / DD / YYYY" or "MMDDYYYY"
   * @returns {Object|null} Error object or null if valid
   */
  validateAge(birthday) {
    if (!birthday) {
      return null; // Required validation is handled separately
    }

    // Parse date from "MM / DD / YYYY" or "MMDDYYYY" format
    let month = null;
    let day = null;
    let year = null;

    // Try to parse "MM / DD / YYYY" format
    const dateMatch = birthday.match(/(\d{1,2})\s*\/\s*(\d{1,2})\s*\/\s*(\d{4})/);
    if (dateMatch) {
      month = parseInt(dateMatch[1], 10);
      day = parseInt(dateMatch[2], 10);
      year = parseInt(dateMatch[3], 10);
    } else {
      // Try to parse "MMDDYYYY" format (unmasked value)
      const unmaskedMatch = birthday.match(/(\d{2})(\d{2})(\d{4})/);
      if (unmaskedMatch) {
        month = parseInt(unmaskedMatch[1], 10);
        day = parseInt(unmaskedMatch[2], 10);
        year = parseInt(unmaskedMatch[3], 10);
      } else {
        // Invalid format
        return this.createError('error_birthday_invalid');
      }
    }

    // Validate date values
    if (month < 1 || month > 12 || day < 1 || day > 31 || year < 1900 || year > 2100) {
      return this.createError('error_birthday_invalid');
    }

    // Calculate age
    const today = new Date();
    let age = today.getFullYear() - year;
    const monthDiff = today.getMonth() - (month - 1);
    const dayDiff = today.getDate() - day;

    // Adjust age if birthday hasn't occurred this year
    if (monthDiff < 0 || (monthDiff === 0 && dayDiff < 0)) {
      age--;
    }

    // Check if age is at least 21
    if (age < 21) {
      return this.createError('error_age_under_21');
    }

    return null;
  }

  /**
   * Validate registration form
   * @param {Object} formData - Form data
   * @param {boolean} skipPasswordValidation - Skip password validation (for SSO users)
   */
  validateRegisterForm(formData, skipPasswordValidation = false) {
    const errors = {};

    // Validate password fields (skip for SSO registration)
    if (!skipPasswordValidation) {
      const passwordErrors = this.validatePasswordFields(
        formData.password,
        formData.password_confirm,
      );
      Object.assign(errors, passwordErrors);
    }

    // Validate required fields
    const requiredData = Object.fromEntries(
      REQUIRED_REGISTER_FIELDS.map((field) => [field, formData[field]]),
    );
    const requiredErrors = this.validateRequired(requiredData);

    if (requiredErrors) {
      Object.assign(errors, requiredErrors);
    }

    // Validate zip code format (letters and digits, max 20 chars, when provided)
    if (formData.zip_code && formData.zip_code.trim() !== '') {
      if (!FormValidator.validateZipCode(formData.zip_code)) {
        errors.zip_code = this.createError('error_zip_code_invalid');
      }
    }

    // Validate age (only if birthday is provided and not empty)
    if (formData.birthday && formData.birthday.trim() !== '') {
      const ageError = this.validateAge(formData.birthday);
      if (ageError) {
        errors.birthday = ageError;
      }
    }

    return FormValidator.hasErrors(errors) ? errors : null;
  }

  /**
   * Validate change password form
   */
  validateChangePasswordForm(password, passwordConfirm) {
    const errors = this.validatePasswordFields(password, passwordConfirm);
    return FormValidator.hasErrors(errors) ? errors : null;
  }
}

export default FormValidator;
